import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ApiBaseService } from './api-base.service';
import { GetCompanyDetailsResultModel, GetContactProjectsResultModel } from 'src/app/interfaces/entities/contact';

export interface ContactRole {
  id: number;
  name: string;
}

@Injectable({
  providedIn: 'root'
})
export class ContactService {

  constructor(private apiService: ApiBaseService) { }

  // Shared across every gpd-role-select instance currently on the page (the service is a
  // singleton), so a role added or renamed in one dropdown — e.g. one project's role field —
  // shows up immediately in every other open one instead of only in the instance that made
  // the change. null means "not fetched yet"; [] is a legitimately empty, loaded list.
  private rolesCache$ = new BehaviorSubject<ContactRole[] | null>(null);
  private rolesLoadPromise: Promise<void> | null = null;

  get roles$(): Observable<ContactRole[] | null> {
    return this.rolesCache$.asObservable();
  }

  async ensureRolesLoaded(): Promise<void> {
    if (this.rolesCache$.value !== null) {
      return; // already loaded (or a load is already in flight, see below)
    }
    if (!this.rolesLoadPromise) {
      this.rolesLoadPromise = this.getContactRoles({})
        .then((result) => {
          const roles: ContactRole[] = (result?.roles ?? []).filter((r: ContactRole) => !!r?.name);
          this.rolesCache$.next(roles);
        })
        .catch(() => this.rolesCache$.next([]));
    }
    return this.rolesLoadPromise;
  }

  // Adds a not-yet-persisted role (id: 0) to the shared list — used right after "add new
  // role" is confirmed. No backend call happens here; the ContactRoles row is only created
  // once the parent form actually submits (addProjectContact/addPerson/etc.).
  addLocalRole(role: ContactRole): void {
    const current = this.rolesCache$.value ?? [];
    if (current.some((r) => r.name.toLowerCase() === role.name.toLowerCase())) {
      return;
    }
    this.rolesCache$.next([...current, role]);
  }

  // Renames a role already present in the shared list by reference — used for both a saved
  // role (after updateContactRole succeeds) and a locally-added, not-yet-persisted one.
  renameRoleInCache(target: ContactRole, newName: string): void {
    const current = this.rolesCache$.value ?? [];
    this.rolesCache$.next(current.map((r) => (r === target ? { ...r, name: newName } : r)));
  }

  getCompanies(payLoad: any): Promise<any> {
    return this.apiService.httpRequest('ContactsService/GetCompanies', payLoad, true, true);
  }

  async getCompanyDetails(payLoad: { CompanyId: number | string }): Promise<GetCompanyDetailsResultModel> {
    const result = await this.apiService.httpRequest('ContactsService/GetCompanyDetails', payLoad, true, true);
    return new GetCompanyDetailsResultModel(result);
  }

  addCompany(payLoad: any): Promise<any> {
    return this.apiService.httpRequest('ContactsService/AddCompany', payLoad, true, true);
  }

  writeCompanyPersonShare(payLoad: any): Promise<any> {
    return this.apiService.httpRequest('ContactsService/WriteCompanyPersonShare', payLoad, true, true);
  }

  removePersonFromCompany(payLoad: any): Promise<any> {
    return this.apiService.httpRequest('ContactsService/RemovePersonFromCompany', payLoad, true, true);
  }

  getPersons(payLoad: any): Promise<any> {
    return this.apiService.httpRequest('ContactsService/GetPersons', payLoad, true, true);
  }

  getPersonDetails(payload: any): Promise<any> {
    return this.apiService.httpRequest('ContactsService/GetPersonDetails', payload, true, true);
  }

  addPerson(payLoad: any): Promise<any> {
    return this.apiService.httpRequest('ContactsService/AddPerson', payLoad, true, true);
  }

  async writeCompanyDetails(payload: any): Promise<any> {
    return this.apiService.httpRequest('ContactsService/WriteCompanyDetails', payload, true, true);
  }

  async writePersonDetails(paylod: any): Promise<any> {
    return this.apiService.httpRequest('ContactsService/WritePersonDetails', paylod, true, true);
  }

  async deleteCompany(payload: any): Promise<any> {
    return this.apiService.httpRequest('ContactsService/DeleteCompany', payload, true, true);
  }

  async deletePerson(payload: any): Promise<any> {
    return this.apiService.httpRequest('ContactsService/DeletePerson', payload, true, true);
  }

  async ConfirmDeletePerson(payload: any): Promise<any> {
    return this.apiService.httpRequest('ContactsService/ConfirmDeletePerson', payload, true, true);
  }

  async getProjectContacts(payload: any): Promise<any> {
    return this.apiService.httpRequest('ContactsService/GetProjectContacts', payload, true, true);
  }

  async getContactProjects(payload: any): Promise<GetContactProjectsResultModel> {
    const result = await this.apiService.httpRequest('ContactsService/GetContactProjects', payload, true, true);
    return new GetContactProjectsResultModel(result);
  }

   async getRelatedContracts(payload: any): Promise<any> {
    return this.apiService.httpRequest("ContactsService/GetRelatedContracts", payload, true, true);
  }

  async addProjectContact(payload: any): Promise<any> {
    return this.apiService.httpRequest('ContactsService/AddProjectContact', payload, true, true);
  }

  async writeProjectContactRole(payload: any): Promise<any> {
    return this.apiService.httpRequest('ContactsService/WriteProjectContactRole', payload, true, true);
  }

  async removeProjectContact(payload: any): Promise<any> {
    return this.apiService.httpRequest('ContactsService/RemoveProjectContact', payload, true, true);
  }

  async getContactRoles(payload: any): Promise<any> {
    return this.apiService.httpRequest('ContactsService/GetContactRoles', payload, true, true);
  }

  async updateContactRole(payload: any): Promise<any> {
    return this.apiService.httpRequest('ContactsService/UpdateContactRole', payload, true, true);
  }
}
